import { inject, injectable } from 'inversify';
import { TOKEN } from '../../../inversify.tokens';
import { TrackLabel } from '../../../types/common';
import Logger from '../../Logger';

export interface GetMediaStreamTrackParams {
  label: TrackLabel;
  constraints: MediaStreamConstraints;
}

@injectable()
class MediaStreamTrackManager {
  readonly #logger: Logger;

  constructor(@inject(TOKEN.LoggerFactory) loggerFactory: (namespace: string) => Logger) {
    this.#logger = loggerFactory('MediaStreamTrackManager');
  }

  async createMediaStreamTrack({ label, constraints }: GetMediaStreamTrackParams): Promise<MediaStreamTrack> {
    if (MediaStreamTrackManager.isDisplayMedia(label)) {
      const [track] = await this.createDisplayMediaTracks(constraints);

      return track;
    }

    const [track] = await this.createUserMediaTracks(constraints);

    return track;
  }

  private static isDisplayMedia(label: TrackLabel): boolean {
    return [TrackLabel.ScreenVideo, TrackLabel.ScreenAudio].includes(label);
  }

  async createUserMediaTracks(constraints: MediaStreamConstraints): Promise<MediaStreamTrack[]> {
    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    this.#logger.debug('createUserMediaTracks() stream created', { streamId: stream.id });

    return stream.getTracks();
  }

  async createDisplayMediaTracks(constraints: MediaStreamConstraints): Promise<MediaStreamTrack[]> {
    const stream = await navigator.mediaDevices.getDisplayMedia(constraints);
    this.#logger.debug('getDisplayMediaTracks() stream created', { streamId: stream.id });

    return stream.getTracks();
  }
}

export default MediaStreamTrackManager;
